import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { getConsent, saveConsent, hasConsent } from "../../utils/cookieConsent";

// X-0.B · Banner de consent de cookies (RGPD).
//
// Aparece ao primeiro visit se non hai entrada en localStorage.cookie_consent.
// 3 botóns directos (acceptAll / rejectAll / configure). O modal "configure"
// permite toggles por categoría (necessary sempre on, analytics e functional
// opcionais).
//
// Renderízase un único elemento; se o usuario xa decidiu, devolve null. Para
// permitir cambiar a decisión despois, expoñerémolo dende o footer (TODO
// follow-up: ligazón "Configurar cookies" no footer que faga clearConsent +
// recarga ou abra o modal directamente).

export default function CookieBanner() {
  const { t } = useTranslation("legal");
  const [show, setShow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    functional: false,
  });

  useEffect(() => {
    if (!hasConsent()) {
      setShow(true);
    } else {
      const c = getConsent();
      if (c?.categories) {
        setPrefs((p) => ({ ...p, ...c.categories, necessary: true }));
      }
    }
  }, []);

  if (!show) return null;

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, functional: true });
    setShow(false);
  };
  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, functional: false });
    setShow(false);
  };
  const saveCustom = () => {
    saveConsent(prefs);
    setShowModal(false);
    setShow(false);
  };
  const togglePref = (key) =>
    setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <>
      <div
        role="dialog"
        aria-label={t("cookies.banner.title")}
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 shadow-2xl z-50"
      >
        <div className="mx-auto max-w-5xl p-4 md:p-6 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="flex-1 text-sm text-zinc-700">
            <h2 className="font-semibold text-zinc-900 mb-1">
              {t("cookies.banner.title")}
            </h2>
            <p>{t("cookies.banner.description")}</p>
            <p className="mt-1 text-xs text-zinc-500">
              <Trans
                i18nKey="cookies.banner.moreInfo"
                t={t}
                components={{
                  1: <Link to="/privacidade" className="underline" />,
                }}
              />
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="px-4 py-2 rounded-lg border border-zinc-300 text-sm hover:bg-zinc-50"
            >
              {t("cookies.banner.configure")}
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="px-4 py-2 rounded-lg border border-zinc-300 text-sm hover:bg-zinc-50"
            >
              {t("cookies.banner.rejectAll")}
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium"
            >
              {t("cookies.banner.acceptAll")}
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-modal-title"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <h2
                id="cookie-modal-title"
                className="text-xl font-semibold mb-2"
              >
                {t("cookies.modal.title")}
              </h2>
              <p className="text-sm text-zinc-600 mb-4">
                {t("cookies.modal.description")}
              </p>

              <CookieCategory
                title={t("cookies.modal.necessary.title")}
                description={t("cookies.modal.necessary.description")}
                alwaysOnLabel={t("cookies.modal.necessary.alwaysOn")}
                disabled
                checked
              />
              <CookieCategory
                title={t("cookies.modal.analytics.title")}
                description={t("cookies.modal.analytics.description")}
                checked={prefs.analytics}
                onToggle={() => togglePref("analytics")}
              />
              <CookieCategory
                title={t("cookies.modal.functional.title")}
                description={t("cookies.modal.functional.description")}
                checked={prefs.functional}
                onToggle={() => togglePref("functional")}
              />

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-zinc-300 text-sm hover:bg-zinc-50"
                >
                  {t("cookies.modal.cancel")}
                </button>
                <button
                  type="button"
                  onClick={saveCustom}
                  className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium"
                >
                  {t("cookies.modal.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CookieCategory({
  title,
  description,
  checked,
  onToggle,
  disabled,
  alwaysOnLabel,
}) {
  return (
    <div className="border border-zinc-200 rounded-xl p-4 mb-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-medium text-zinc-900">{title}</h3>
          <p className="text-sm text-zinc-600 mt-1">{description}</p>
        </div>
        <div className="shrink-0 pt-1">
          {disabled ? (
            <span className="text-xs text-zinc-500 whitespace-nowrap">
              {alwaysOnLabel}
            </span>
          ) : (
            <button
              type="button"
              onClick={onToggle}
              role="switch"
              aria-checked={checked}
              aria-label={title}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                checked ? "bg-rose-600" : "bg-zinc-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  checked ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
